import {NucleotideType, PolymerType, ResidueType} from 'biojs/BioConstants';
import ModelTypeConstants, {SequenceSubtype} from 'constants/ModelTypeConstants';
import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import {Strand as GQLStrand} from 'graphql-client/__generated/graphqlGlobalTypes';

export function convertMolBioStrandToGQLStrand(strand: Strand): GQLStrand {
  const mappedStrands = {
    1: GQLStrand.FORWARD,
    0: GQLStrand.NONE,
    [-1]: GQLStrand.REVERSE,
  };
  return mappedStrands[strand];
}

export function convertGQLStrandToMolBioStrand(strand: GQLStrand): Strand {
  const mappedStrands: {[key in GQLStrand]: Strand} = {
    [GQLStrand.FORWARD]: 1,
    [GQLStrand.NONE]: 0,
    [GQLStrand.REVERSE]: -1,
  };
  return mappedStrands[strand];
}

export type Strand = -1 | 0 | 1;
export type StrandOfDoubleStranded = -1 | 1;
export interface Cutsite {
  position: number;
  strand: Strand;
}

export function convertStrandToDisplayName(strand: Strand): string {
  const strandToName = {
    1: 'Forward',
    [-1]: 'Reverse',
    0: 'None',
  };
  return strandToName[strand];
}

// Typesafe conversion of a strand to its opposite. Previously, some code would just multiply by negative 1,
// but that required casts to satisfy TypeScript.
export const OPPOSITE_STRAND_OF_DOUBLE_STRANDED: Record<StrandOfDoubleStranded, StrandOfDoubleStranded> = {
  '1': -1,
  '-1': 1,
};

export const OPPOSITE_STRAND: Record<Strand, Strand> = {
  ...OPPOSITE_STRAND_OF_DOUBLE_STRANDED,
  '0': 0,
};

export interface CustomField {
  name: string;
  value: string;
}

export type Side = 'start' | 'end';

export const OPPOSITE_SIDE: Record<Side, Side> = {
  start: 'end',
  end: 'start',
};

export enum PolymerSubtype {
  dna_sequence = 'dna_sequence',
  rna_sequence = 'rna_sequence',
  dna_oligo = 'dna_oligo',
  rna_oligo = 'rna_oligo',
  aa_sequence = 'aa_sequence',
}

export const POLYMER_SUBTYPE_TO_POLYMER_TYPE: Record<PolymerSubtype, PolymerType> = {
  [PolymerSubtype.dna_sequence]: NucleotideType.DNA,
  [PolymerSubtype.rna_sequence]: NucleotideType.RNA,
  [PolymerSubtype.dna_oligo]: NucleotideType.DNA,
  [PolymerSubtype.rna_oligo]: NucleotideType.RNA,
  [PolymerSubtype.aa_sequence]: ResidueType.AA,
};

export const SEQUENCE_SUBTYPE_TO_POLYMER_SUBTYPE: Record<SequenceSubtype, PolymerSubtype> = {
  [ModelTypeConstants.SEQUENCE]: PolymerSubtype.dna_sequence,
  [ModelTypeConstants.RNA_SEQUENCE]: PolymerSubtype.rna_sequence,
  [ModelTypeConstants.DNA_OLIGO]: PolymerSubtype.dna_oligo,
  [ModelTypeConstants.RNA_OLIGO]: PolymerSubtype.rna_oligo,
};

export const OLIGO_NUCLEOTIDE_TYPE_TO_POLYMER_SUBTYPE: Record<NucleotideType, PolymerSubtype> = {
  [NucleotideType.DNA]: PolymerSubtype.dna_oligo,
  [NucleotideType.RNA]: PolymerSubtype.rna_oligo,
};

export const LONG_SEQUENCE_NUCLEOTIDE_TYPE_TO_POLYMER_SUBTYPE: Record<NucleotideType, PolymerSubtype> = {
  [NucleotideType.DNA]: PolymerSubtype.dna_sequence,
  [NucleotideType.RNA]: PolymerSubtype.rna_sequence,
};

export const POLYMER_SUBTYPE_TO_MODEL_TYPE: Record<PolymerSubtype, OligoAwareFolderItemType> = {
  [PolymerSubtype.dna_sequence]: ModelTypeConstants.SEQUENCE,
  [PolymerSubtype.rna_sequence]: ModelTypeConstants.RNA_SEQUENCE,
  [PolymerSubtype.dna_oligo]: ModelTypeConstants.DNA_OLIGO,
  [PolymerSubtype.rna_oligo]: ModelTypeConstants.RNA_OLIGO,
  [PolymerSubtype.aa_sequence]: ModelTypeConstants.PROTEIN,
};

export const POLYMER_SUBTYPE_TO_DISPLAY_NAME: Record<PolymerSubtype, string> = {
  [PolymerSubtype.dna_sequence]: 'DNA sequence',
  [PolymerSubtype.rna_sequence]: 'RNA sequence',
  [PolymerSubtype.dna_oligo]: 'DNA oligo',
  [PolymerSubtype.rna_oligo]: 'RNA oligo',
  [PolymerSubtype.aa_sequence]: 'AA sequence',
};
